import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, MessageCircle, Inbox, Archive } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const API = '/api';

const TABS = [
  { key: 'active', label: 'Primary', icon: Inbox },
  { key: 'request', label: 'Requests', icon: MessageCircle },
  { key: 'closed', label: 'History', icon: Archive },
];

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d`;
  return new Date(dateString).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const MessageSidebar = ({ activeTab, onTabChange, selectedConvId, onSelectConversation, userId }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/messages/conversations?status=${activeTab}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(Array.isArray(res.data) ? res.data : []);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Expose refresh for parent
  useEffect(() => {
    window.__refreshMessageSidebar = fetchConversations;
    return () => { delete window.__refreshMessageSidebar; };
  }, [fetchConversations]);

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const filtered = searchQuery
    ? conversations.filter((c) => {
        const q = searchQuery.toLowerCase();
        const name = (c.other_user?.display_name || '').toLowerCase();
        const handle = (c.other_user?.handle || '').toLowerCase();
        return name.includes(q) || handle.includes(q);
      })
    : conversations;

  // Count requests for badge
  const [requestCount, setRequestCount] = useState(0);
  useEffect(() => {
    const fetchRequestCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/messages/conversations?status=request&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRequestCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch { /* */ }
    };
    fetchRequestCount();
  }, [activeTab]);

  return (
    <div className="flex flex-col h-full bg-zinc-950 border-r border-zinc-800" data-testid="message-sidebar">
      {/* Header */}
      <div className="px-4 py-4 border-b border-zinc-800">
        <h2 className="text-lg font-bold text-white mb-3">Messages</h2>
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search conversations..."
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
            data-testid="sidebar-search-input"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800" data-testid="sidebar-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => onTabChange(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors relative ${
              activeTab === key
                ? 'text-orange-400 border-b-2 border-orange-400'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`tab-${key}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {key === 'request' && requestCount > 0 && (
              <span className="bg-orange-500 text-white text-[9px] rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                {requestCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <MessageCircle className="w-10 h-10 text-zinc-700 mb-2" />
            <p className="text-zinc-500 text-sm">
              {activeTab === 'active' && 'No active conversations'}
              {activeTab === 'request' && 'No pending requests'}
              {activeTab === 'closed' && 'No conversation history'}
            </p>
          </div>
        ) : (
          filtered.map((conv) => {
            const other = conv.other_user || {};
            const isSelected = conv.id === selectedConvId;
            const hasUnread = (conv.unread_count || 0) > 0;
            const isRequest = activeTab === 'request';
            const isIncoming = isRequest && conv.initiated_by !== userId;
            const preview = conv.last_message?.content || (isRequest ? 'Message request' : '');

            return (
              <button
                key={conv.id}
                onClick={() => onSelectConversation(conv)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isSelected ? 'bg-zinc-800' : hasUnread ? 'bg-zinc-900/60 hover:bg-zinc-800/60' : 'hover:bg-zinc-900/40'
                }`}
                data-testid={`conv-row-${conv.id}`}
              >
                <Avatar className="h-10 w-10 flex-shrink-0">
                  {other.avatar_url && <AvatarImage src={other.avatar_url} alt={other.display_name} />}
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-sm font-bold">
                    {(other.display_name || other.handle || 'U')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm truncate ${hasUnread ? 'font-semibold text-white' : 'text-zinc-300'}`}>
                      {other.display_name || other.handle || 'Creator'}
                    </p>
                    <span className="text-[10px] text-zinc-500 flex-shrink-0 ml-2">
                      {formatTimeAgo(conv.updated_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className={`text-xs truncate ${hasUnread ? 'text-zinc-300' : 'text-zinc-500'}`}>
                      {isRequest && isIncoming ? '📩 ' : ''}{preview}
                    </p>
                    {hasUnread && (
                      <span className="flex-shrink-0 ml-2 bg-orange-500 text-white text-[9px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MessageSidebar;
